import { STEP } from "#src/constants/step.ts";
import { defineStore } from "#src/stores/state-wrapper.js";

import { useEappStore } from "#src/stores/electronic-application.js";
import { useStepBirthdateStore } from "#src/stores/step-birthdate.js";
import { useStepCompareStore } from "#src/stores/step-compare.js";
import { useStepBenefitEstimationStore } from "#src/stores/step-benefit-estimation.js";
import { useStepQuoteStore } from "#src/stores/step-quote.js";
import { useStepPreQuoteInformationStore } from "#src/stores/step-pre-quote-information.js";
import { usePrimaryInsuredStore } from "#src/stores/insured.js";
import { useInstanceSettingsStore } from "#src/stores/instance-settings.js";

import {
  FINAL_EXPENSE_MAX_AGE,
  FINAL_EXPENSE_MIN_AGE,
} from "#src/types/quote-params/FinalExpense.ts";

import { differenceInMonths } from "date-fns/differenceInMonths";
import { parse } from "date-fns/parse";
import { PRODUCTS } from "#src/constants/products.ts";

export const PRIORITIES = {
  TERM: "term",
  ROP_TERM: "rop_term",
  PERMANENT: "permanent",
  SUPPLEMENT_RETIREMENT: "supplement_retirement",
  GROWTH: "growth",
  INCOME_RIDER: "income_rider",
  BURIAL_EXPENSES: "burial_expenses",
  LTC: "ltc",
  ACCIDENTAL_DEATH: "accidental_death",
  GUARANTEED_ISSUE: "guaranteed_issue",
};

export const useStepLifePriorityStore = (pinia, hot) =>
  defineStore("step-life-priority", {
    getters: {
      step: () => STEP.LIFE_INSURANCE_PRIORITY,
      next() {
        const eAppStore = useEappStore(pinia);
        const priority = eAppStore.assistance.topPriority;
        const comparePriorities = [
          PRIORITIES.TERM,
          PRIORITIES.ROP_TERM,
          PRIORITIES.PERMANENT,
          PRIORITIES.LTC,
        ];
        const benefitEstimationPriorities = [
          PRIORITIES.SUPPLEMENT_RETIREMENT,
          PRIORITIES.GROWTH,
        ];
        const preQuoteInformationStepPriorities = [PRIORITIES.INCOME_RIDER];
        let goToProtection = false;

        if (!priority || comparePriorities.includes(priority)) {
          goToProtection = true;
        }
        const goToEst = benefitEstimationPriorities.includes(priority);
        const goToInfo = preQuoteInformationStepPriorities.includes(priority);
        const compareStep = useStepCompareStore(pinia);
        if (goToProtection) return compareStep.step;

        const benefit = useStepBenefitEstimationStore(pinia);
        if (goToEst) return benefit.step;

        const preQuoteInfo = useStepPreQuoteInformationStore(pinia);
        if (goToInfo) return preQuoteInfo.step;

        const quote = useStepQuoteStore(pinia);
        return quote.step;
      },
      previous: () => {
        const birthdate = useStepBirthdateStore(pinia);
        if (birthdate.inactive) return birthdate.previous;
        return birthdate.step;
      },
      skippable: () => {
        const eApp = useEappStore(pinia);
        return !eApp.assistance.topPriority;
      },
      availablePriorities() {
        const instance = useInstanceSettingsStore(pinia);
        const insuredStore = usePrimaryInsuredStore(pinia);

        let age = null;
        if (insuredStore.birthdate.model) {
          age =
            differenceInMonths(
              new Date(),
              parse(insuredStore.birthdate.model, "yyyy-MM-dd", new Date())
            ) / 12;
        }
        const priorities = [];

        if (instance.products[PRODUCTS.TERM] && age >= 17.5 && age <= 80) {
          priorities.push(PRIORITIES.TERM);
        }

        if (instance.products[PRODUCTS.ROP_TERM] && age >= 17.5 && age <= 65) {
          priorities.push(PRIORITIES.ROP_TERM);
        }

        if (instance.permanentAvailable.length > 0) {
          priorities.push(PRIORITIES.PERMANENT);
        }

        if (instance.products[PRODUCTS.ACCUMULATION_IUL]) {
          priorities.push(PRIORITIES.SUPPLEMENT_RETIREMENT);
        }

        if (instance.products[PRODUCTS.WHOLE_LIFE]) {
          priorities.push(PRIORITIES.GROWTH);
        }

        if (instance.products[PRODUCTS.INCOME_RIDER] && age >= 40) {
          priorities.push(PRIORITIES.INCOME_RIDER);
        }

        if (
          instance.products[PRODUCTS.FINAL_EXPENSE] &&
          age >= FINAL_EXPENSE_MIN_AGE &&
          age <= FINAL_EXPENSE_MAX_AGE
        ) {
          priorities.push(PRIORITIES.BURIAL_EXPENSES);
        }

        const defaultLtcConfig = [
          PRODUCTS.LINKED_BENEFIT,
          PRODUCTS.LTC,
          PRODUCTS.PROTECTION_IUL,
          PRODUCTS.PROTECTION_VUL,
          PRODUCTS.UNIVERSAL_LIFE,
          PRODUCTS.WHOLE_LIFE,
        ];

        const defaultLtcConfigNoVul = [
          PRODUCTS.LINKED_BENEFIT,
          PRODUCTS.LTC,
          PRODUCTS.PROTECTION_IUL,
          PRODUCTS.UNIVERSAL_LIFE,
          PRODUCTS.WHOLE_LIFE,
        ];

        const focusedLtcConfig = [
          PRODUCTS.LTC,
          PRODUCTS.UNIVERSAL_LIFE,
          PRODUCTS.LINKED_BENEFIT,
        ];

        const ltcAvailable = focusedLtcConfig.some(p => instance.products[p]);
        if (ltcAvailable && age < 80 && age >= 17.5) {
          priorities.push(PRIORITIES.LTC);
        }

        if (
          instance.products[PRODUCTS.ACCIDENTAL_DEATH] &&
          age >= 18 &&
          age <= 49
        ) {
          priorities.push(PRIORITIES.ACCIDENTAL_DEATH);
        } else if (
          instance.products[PRODUCTS.FINAL_EXPENSE] &&
          age >= 50 &&
          age <= 80
        ) {
          priorities.push(PRIORITIES.GUARANTEED_ISSUE);
        }

        const showingDefaultLtcConfig = [
          defaultLtcConfig,
          defaultLtcConfigNoVul,
          focusedLtcConfig,
        ].find(
          config =>
            instance.available.length === config.length &&
            config.every(v => instance.products[v])
        );

        if (showingDefaultLtcConfig) {
          return [PRIORITIES.LTC];
        }

        return priorities;
      },
    },
  })(pinia, hot);
